import rfid from '../../../../assets/icons/rfid.svg';

import * as React from 'react';
import RfidChipIdForm from './RfidChipIdForm';
import * as R from 'ramda';
import ModalView, { ModalContent } from '../../../../layout/modal/ModalView';
import { ChipIdSet } from '../passPrinting/chipId';
import { isNilOrEmpty, assoc } from '../../../../language/module';
import { findRfidAssocError } from './module';
import { MODAL_SIZE } from '../../../../layout/modal/modalSize';
import { connect } from 'react-redux';
import { GuestDetails } from '../../../guestDetails';
import { IAppState } from '../../../../core/redux/IAppState';
import { fullName } from '../../../module';
import { assocRfidAction } from './assocRfidAction';
import { getMostRecentMedia } from '../module';
import ModalTitleHeader from '../../../../layout/modal/ModalTitleHeader';
import { getResource } from '../../../../language/resources/getResource';


/**
 * AssociateRfidModal: a component that renders a RFID Chip Id form within a modal. This form manages associating a
 * guest's abstract Ikon pass with the RFID code on the physical card. On code submission, this component runs
 * validation to ensure the code is not a duplicate and is 20 digits long.
 *
 * @firstName           - The first name of the guest. Passed in for rendering.
 * @lastName            - The last name of the guest. Passed in for rendering.
 * @knownGuestRfidCodes - A set of known chip IDs. Used for validation. Structure built initially when guests are added from db.
 * @hasGuestMedia     - Flag showing if guest has media associated.
 * @onDismiss           - Dismisses modal.
 * @onSubmit            - Submits form
 *
 * PureComponent: true
 * Connected to Redux: true
 */
export default connect(
  (state: IAppState, props: any) => ({
    guest:               state.guests.get(props.guestId),
    currentRfid:         R.path([ 'chipID' ], getMostRecentMedia(state.guests.get(props.guestId))) || '',
    knownGuestRfidCodes: state.knownGuestRfidCodes,
    resources:           state.locale,
  }),
  (dispatch) => ({
    onSubmit: (
                guest: GuestDetails,
                chipId: string,
              ) => dispatch(assocRfidAction(guest, assoc('chipID', chipId, getMostRecentMedia(guest)!))),
  }),
)(class AssociateRfidModal extends React.PureComponent<{
  guestId: string;
  guest?: GuestDetails;
  currentRfid?: string;
  knownGuestRfidCodes?: ChipIdSet | undefined;
  resources: Record<string, any>;
  onDismiss: () => void;
  onSubmit?: (guest: GuestDetails, chipId: string) => void;
}, {
  chipId: string;
  chipFormError: string | undefined;
  reprintMessage: string | undefined;
}> {
  public state = {
    chipId:         '',
    chipFormError:  '',
    reprintMessage: '',
  };


  constructor(props) {
    super(props);

    if (isNilOrEmpty(this.props.guest!.media)) {
      this.state.reprintMessage = getResource(this.props.resources, 'ASSOC_RFID_REPRINT_MESSAGE_NO_ID');

    } else if (!isNilOrEmpty(this.props.currentRfid)) {
      this.state.reprintMessage = getResource(this.props.resources, 'ASSOC_RFID_REPRINT_MESSAGE_HAS_ID', this.props.currentRfid);
    }
  }


  public onSubmit = () => {
    const rfidError = findRfidAssocError(this.props.resources, this.props.guestId, this.state.chipId, this.props.knownGuestRfidCodes!);

    this.setState(prev => assoc('chipFormError', rfidError, prev));

    if (!rfidError) {
      this.props.onSubmit!(this.props.guest!, this.state.chipId);
      this.props.onDismiss();
    }
  };


  /**
   * Triggered when the user changes the RFID code value in the text field.
   */
  public onCodeChange = (newCode: string): void => this.setState(prev => R.assocPath([ 'chipId' ], newCode, prev));


  public render = () => (
    <ModalView
      size={MODAL_SIZE.SMALL}
      onDismiss={this.props.onDismiss}>
      <ModalTitleHeader resources={this.props.resources} heading={getResource(this.props.resources, 'ASSOC_RFID_MODAL_HEADER')} icon={rfid} onDismiss={this.props.onDismiss} />
      <ModalContent>
        <RfidChipIdForm onClose={this.props.onDismiss}
                        onSubmit={this.onSubmit}
                        onChipIdUpdate={this.onCodeChange}
                        chipId={this.state.chipId}
                        guestName={fullName(this.props.guest)}
                        error={this.state.chipFormError}
                        passIsLinked={!isNilOrEmpty(this.state.reprintMessage)}
                        passIsLinkedMsg={this.state.reprintMessage}
                        resources={this.props.resources}/>
      </ModalContent>
    </ModalView>
  );
});
